import { motion, AnimatePresence, Variants } from "framer-motion";
import { CustomizationCategory } from "../../character.types";
import categoryImg1 from "../../assets/images/categories/1.png";
import categoryImg2 from "../../assets/images/categories/2.png";
import categoryImg3 from "../../assets/images/categories/3.png";
import categoryImg4 from "../../assets/images/categories/4.png";
import categoryImg5 from "../../assets/images/categories/5.png";
import activeCategoryBg from "../../assets/images/active-category-bg.png";
import defaultCategoryBg from "../../assets/images/default-category-bg.png";
import saveIcon from "../../assets/images/save-icon.png";
import { useCharacter } from "../../hooks/use-character";
import { useAudioEffects } from "../../../../hooks/use-audio-effects";
import clickSound from "../../../../assets/audio/click.wav";
import { useEffect } from "react";

interface Props {
  className?: string;
  onCategoryClick: (category: CustomizationCategory) => void;
  activeCategoryId: string;
}

const CATEGORIES: CustomizationCategory[] = [
  { id: "identity", name: "Identity", iconSrc: categoryImg1, iconWidth: 64 },
  { id: "face", name: "Face", iconSrc: categoryImg2, iconWidth: 50 },
  { id: "body", name: "Body", iconSrc: categoryImg3, iconWidth: 35 },
  { id: "hair", name: "Hair", iconSrc: categoryImg4, iconWidth: 64 },
  { id: "beard", name: "Beard", iconSrc: categoryImg5, iconWidth: 40 },
];

export const DEFAULT_ACTIVE_CATEGORY = CATEGORIES[0];

// ─── Variants ────────────────────────────────────────────────────────────────

/**
 * Container: propagates stagger to every card child.
 */
const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.05,
    },
  },
};

/**
 * Card entry: blur + scale + y "materialise" effect.
 * easeOutExpo settles gracefully after a sharp initial acceleration.
 */
const cardVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.78,
    y: 22,
    filter: "blur(6px)",
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/**
 * Floating icon: infinite sinusoidal y loop while active.
 * "idle" snaps back to rest with a short ease-out.
 * NOTE: axis is x here since the layout is vertical (cards stack top→bottom),
 * so a subtle x nudge reads better than y which would fight the card stack.
 * Swap back to y if you prefer the original behaviour.
 */
const floatVariants: Variants = {
  idle: {
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
  floating: {
    y: [0, -5, 0],
    transition: {
      duration: 2.8,
      repeat: Infinity,
      repeatType: "loop",
      ease: "easeInOut",
    },
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

const CustomizationCategories = ({
  className = "",
  onCategoryClick,
  activeCategoryId,
}: Props) => {
  const { onSave, mode } = useCharacter();
  const { playEffect } = useAudioEffects();

  useEffect(() => {
    if (mode === "operator") {
      onCategoryClick(CATEGORIES[1]);
    }
  }, [mode]);

  return (
    <motion.div
      // ↓ Smanjen gap na manjim ekranima
      className={`flex flex-col gap-1.5 mid:gap-3 ${className}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {CATEGORIES.map((category) => {
        const isActive = category.id === activeCategoryId;

        if (category.id === "identity" && mode === "operator") return null;

        return (
          <motion.div
            key={category.id}
            className="relative overflow-hidden cursor-pointer select-none w-fit rounded-md"
            variants={cardVariants}
            whileHover={{
              scale: 1.05,
              transition: { type: "spring", stiffness: 360, damping: 20 },
            }}
            whileTap={{
              scale: 0.95,
              transition: { type: "spring", stiffness: 500, damping: 28 },
            }}
            onClick={() => {
              playEffect(clickSound);
              onCategoryClick(category);
            }}
          >
            <img
              src={defaultCategoryBg}
              alt=""
              aria-hidden
              draggable={false}
              className="absolute inset-0 w-full h-full object-fill rounded-md pointer-events-none select-none"
            />

            <AnimatePresence mode="sync">
              {isActive && (
                <motion.img
                  key="active-bg"
                  src={activeCategoryBg}
                  alt=""
                  aria-hidden
                  draggable={false}
                  className="absolute inset-0 w-full h-full object-fill rounded-md pointer-events-none select-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.38, ease: "easeInOut" }}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {isActive && (
                <motion.div
                  key="active-glow"
                  className="absolute inset-0 pointer-events-none rounded-md"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  style={{
                    background:
                      "radial-gradient(ellipse at 50% 95%, rgba(255,255,255,0.10) 0%, transparent 70%)",
                    boxShadow: "inset 0 0 32px rgba(255,255,255,0.05)",
                  }}
                />
              )}
            </AnimatePresence>

            <motion.div
              className="absolute inset-0 pointer-events-none rounded-md"
              initial={{ opacity: 0 }}
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.22 }}
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 52%, rgba(255,255,255,0.025) 100%)",
              }}
            />

            {/* ── Content: manje dimenzije na laptop ekranima ── */}
            <div className="relative z-10 flex flex-col justify-between items-center min-w-[85px] min-h-[85px] mid:min-w-[110px] mid:min-h-[110px]  2560px:min-h-[130px]  2560px:min-w-[130px] p-1.5 mid:p-2">
              <div />

              <motion.div
                className="flex items-center justify-center"
                variants={floatVariants}
                animate={isActive ? "floating" : "idle"}
              >
                <motion.img
                  src={category.iconSrc}
                  alt="icon"
                  loading="eager"
                  width={category.iconWidth}
                  height={50}
                  // ↓ Ograniči ikonu na manjim ekranima
                  className="object-contain max-w-[38px] mid:max-w-none 2560px:max-w-[50px]"
                  animate={{
                    scale: isActive ? 1.1 : 1,
                    filter: isActive
                      ? "drop-shadow(-5px 7px 12px rgba(255,255,255,0.44)) drop-shadow(0px 0px 10px rgba(255,255,255,0.18))"
                      : "drop-shadow(-5px 7px 6px rgba(255,255,255,0.25))",
                  }}
                  transition={{
                    scale: { type: "spring", stiffness: 300, damping: 18 },
                    filter: { duration: 0.35, ease: "easeOut" },
                  }}
                />
              </motion.div>

              {/* ↓ Manji tekst na laptopima */}
              <motion.p
                className="text-[0.72rem] mid:text-[0.85rem] 2560px:text-[1rem] text-center mt-1 mid:mt-2"
                animate={{
                  opacity: isActive ? 1 : 0.6,
                  y: isActive ? 0 : 1,
                  color: isActive
                    ? "rgba(255, 255, 255, 1)"
                    : "rgba(255, 255, 255, 0.6)",
                }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                {category.name}
              </motion.p>
            </div>
          </motion.div>
        );
      })}

      {/* ── Save Card: isto responsivno skaliranje ── */}
      <motion.div
        onClick={() => {
          playEffect(clickSound);
          onSave();
        }}
        // ↓ min/max-w responsive
        className="relative overflow-hidden flex-1 w-fit min-w-[85px] max-w-[85px] mid:min-w-[110px] mid:max-w-[110px] 2560px:min-w-[130px] rounded-md cursor-pointer select-none"
        variants={cardVariants}
        whileHover={{
          scale: 1.005,
          transition: { type: "spring", stiffness: 360, damping: 20 },
        }}
        whileTap={{
          scale: 0.95,
          transition: { type: "spring", stiffness: 500, damping: 28 },
        }}
      >
        <img
          src={defaultCategoryBg}
          alt=""
          aria-hidden
          draggable={false}
          className="absolute inset-0 w-full h-full object-fill rounded-md pointer-events-none select-none"
        />

        <motion.div
          className="absolute inset-0 pointer-events-none rounded-md"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.22 }}
          style={{
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 52%, rgba(255,255,255,0.025) 100%)",
          }}
        />

        <div className="relative z-10 p-1.5 mid:p-2 flex flex-col justify-between items-center space-y-1.5 mid:space-y-2 h-full">
          <div />
          <img
            src={saveIcon}
            alt="icon"
            loading="eager"
            width={50}
            height={50}
            // ↓ Isto ograničenje kao kategorije
            className="object-contain max-w-[38px] mid:max-w-none 2560px:max-w-[50px]"
          />
          <p className="text-[0.72rem] mid:text-[0.9rem] 2560px:text-[1rem]  text-white text-center">
            Save character
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CustomizationCategories;
